import { useQuery } from "@tanstack/react-query";
import type { FeatureCollection } from "geojson";
import type { ComponentType } from "react";

import { ApiError, api, background } from "../../api/client";
import { tokenColor } from "../../components/theme";
import { useUi } from "../../state/ui";
import { countrySheetKey } from "../../views/country/countryJob";
import { revealPaint, useMapRevealed } from "../reveal";
import { BORDERS_BELOW, bordersState, type BordersState, countryBorderLayers } from "./countryBorders";

/** Calque des frontières des pays fictifs de la fiche pays (demande d'Alexis du 11/10/2026, seule exception à la
 *  carte figée) : couches et lecture des données. Le calque suit la fiche : sa clé porte l'empreinte de la carte de
 *  la fiche (`map.v`), une fiche régénérée ou recomposée relit les frontières. */

interface EngineParts {
  Source: ComponentType<any>;
  Layer: ComponentType<any>;
}

/** Gris foncé discret des autres frontières et des noms des pays voisins, halo blanc des libellés (comme les autres
 *  libellés de la carte). */
const GREY = "#5b6170";
const HALO = "#ffffff";

/** Fiche pays du dossier (même lecture et même cache que la vue « Fiche pays ») et ses frontières. */
export function useCountryBorders(): { state: BordersState; borders: FeatureCollection | undefined } {
  const exerciseId = useUi((s) => s.exerciseId);
  const sheet = useQuery({
    queryKey: countrySheetKey(exerciseId),
    queryFn: () => background(() => api.countrySheet(exerciseId as string)),
    enabled: Boolean(exerciseId),
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 2,
    staleTime: 60_000,
  });
  const v = sheet.data?.map?.v ?? null;
  const borders = useQuery({
    queryKey: ["country-borders", exerciseId, v],
    queryFn: () => background(() => api.countryBorders(exerciseId as string, v as string)),
    enabled: Boolean(exerciseId && v),
    staleTime: Infinity,
    placeholderData: (previous) => previous,
  });
  const missing = sheet.error instanceof ApiError && sheet.error.status === 404;
  return {
    state: bordersState({ exerciseId, hasData: Boolean(sheet.data), missing }),
    borders: borders.data,
  };
}

export function CountryBordersLayers({ Source, Layer }: EngineParts) {
  const visible = useUi((s) => s.controlVisible.countryBorders !== false);
  const revealed = useMapRevealed();
  const { state, borders } = useCountryBorders();
  if (state !== "ready" || !borders || !borders.features.length) return null;
  const layers = countryBorderLayers({ hostile: tokenColor("hostile"), grey: GREY, ink: tokenColor("ink"), halo: HALO });
  return (
    <Source id="gx-country-borders" type="geojson" data={borders}>
      {layers.map((layer) => (
        <Layer
          key={layer.id}
          id={layer.id}
          type={layer.type}
          beforeId={BORDERS_BELOW}
          filter={layer.filter}
          // Mapbox GL refuse une borne de zoom `undefined` : la clé n'est posée que si elle existe.
          {...(layer.maxzoom !== undefined ? { maxzoom: layer.maxzoom } : {})}
          layout={{ ...(layer.layout ?? {}), visibility: visible ? "visible" : "none" }}
          paint={revealPaint(layer.type, layer.paint, revealed)}
        />
      ))}
    </Source>
  );
}
